import React from 'react';
import { number, objectOf, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';

import { ResponsiveImage } from '../../../../components/index.js';
import css from './CustomAppearance.module.css';

export const CustomAppearance = React.forwardRef((props, ref) => {
  const {
    className,
    rootClassName,
    backgroundColor,
    backgroundImage,
    backgroundImageOverlay,
    alt,
    sizes,
  } = props;

  // ---- VALIDATION -----------------------------------------------------
  const hasImage =
    backgroundImage &&
    backgroundImage.attributes &&
    backgroundImage.attributes.variants &&
    Object.keys(backgroundImage.attributes.variants).length > 0;

  // overlay is valid ONLY if preset is not "none"
  const validOverlay =
    backgroundImageOverlay &&
    backgroundImageOverlay.preset &&
    backgroundImageOverlay.preset !== 'none'
      ? backgroundImageOverlay
      : null;

  // fallback color only if no image
  const safeBackgroundColor = hasImage ? null : backgroundColor || '#000000';

  // --- HARD EXIT: if NOTHING to render, return null early (SSR safe) ---
  if (!hasImage && !safeBackgroundColor && !validOverlay) {
    return null;
  }

  const getVariantNames = img => {
    const { variants } = img?.attributes || {};
    return variants ? Object.keys(variants) : [];
  };

  const classes = classNames(rootClassName || css.backgroundImageWrapper, className);

  const isBrowser = typeof window !== 'undefined';

  const overlayStyle = validOverlay
    ? {
        backgroundColor: validOverlay.color,
        opacity: validOverlay.opacity,
      }
    : null;

  return (
    <div
      className={classes}
      style={safeBackgroundColor ? { backgroundColor: safeBackgroundColor } : null}
    >
      {/* IMAGE (browser only) */}
      {hasImage && isBrowser ? (
        <ResponsiveImage
          className={css.backgroundImage}
          ref={ref}
          alt={alt}
          image={backgroundImage}
          variants={getVariantNames(backgroundImage)}
          sizes={sizes}
        />
      ) : null}

      {/* OVERLAY */}
      {overlayStyle ? (
        <div className={css.backgroundOverlay} style={overlayStyle} />
      ) : null}
    </div>
  );
});

CustomAppearance.displayName = 'CustomAppearance';

CustomAppearance.defaultProps = {
  rootClassName: null,
  className: null,
  alt: 'background image',
  sizes: null,
  backgroundColor: null,
  backgroundImage: null,
  backgroundImageOverlay: null,
};

CustomAppearance.propTypes = {
  rootClassName: string,
  className: string,
  backgroundColor: string,
  backgroundImage: shape({
    id: string.isRequired,
    type: oneOf(['imageAsset']).isRequired,
    attributes: shape({
      variants: objectOf(
        shape({
          width: number.isRequired,
          height: number.isRequired,
          url: string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }),
  backgroundImageOverlay: shape({
    preset: string.isRequired,
    color: string,
    opacity: number,
  }),
  alt: string,
  sizes: string,
};
